/**
 * Image URL rewriting between bundle storage form and editor display form.
 *
 * The deployed bundle stores in-site images as relative paths
 * (`/images/foo.jpg`) so the file on git is portable across hostnames.
 * In the editor — running on `manage.lndng.dev` (or localhost in dev) —
 * those relative paths resolve against the editor's origin, where the
 * images don't exist. We rewrite to absolute URLs against the site's
 * deployed host before handing the data to Puck, and reverse the
 * rewrite when persisting back so the saved bundle stays portable.
 *
 * R2-temp upload URLs (full https://… pointing at the temp bucket) are
 * pass-through both directions: the backend's `extractR2ImageKeys`
 * handles those on save.
 */

const IMAGES_PREFIX = '/images/';

export function absolutizeImageUrls<T>(value: T, siteHost: string): T {
  const base = stripTrailingSlash(siteHost);
  if (!base) return value;
  return mapStrings(value, (s) =>
    s.startsWith(IMAGES_PREFIX) ? `${base}${s}` : s,
  );
}

export function relativizeImageUrls<T>(value: T, siteHost: string): T {
  const base = stripTrailingSlash(siteHost);
  if (!base) return value;
  const prefix = `${base}${IMAGES_PREFIX}`;
  return mapStrings(value, (s) =>
    s.startsWith(prefix) ? s.slice(base.length) : s,
  );
}

function stripTrailingSlash(s: string): string {
  return s.endsWith('/') ? s.slice(0, -1) : s;
}

/**
 * Deep map any string leaf in an arbitrary value through `transform`.
 * Preserves structure; returns a new value when anything changes,
 * the original reference otherwise (small optimization for memoizers).
 */
function mapStrings<T>(value: T, transform: (s: string) => string): T {
  if (typeof value === 'string') {
    const next = transform(value);
    return (next === value ? value : next) as T;
  }
  if (Array.isArray(value)) {
    let changed = false;
    const out = value.map((item) => {
      const next = mapStrings(item, transform);
      if (next !== item) changed = true;
      return next;
    });
    return (changed ? out : value) as T;
  }
  if (value && typeof value === 'object') {
    let changed = false;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const next = mapStrings(v, transform);
      if (next !== v) changed = true;
      out[k] = next;
    }
    return (changed ? (out as T) : value);
  }
  return value;
}
